import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Card from "components/Card/Card";
import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import KaboodleToaster from "components/common/KaboodleToaster";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import MaskedInput from "components/inputs/TextInput/MaskedInput";
import KaboodleModal from "components/Modals/KaboodleModal";
import SectionBreak from "components/SectionBreak";
import Tooltip from "components/Tooltip/Tooltip";
import type { RequestToVacate } from "pages/liens/LienTypes";
import { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useCreateRequestToVacateMutation,
  useDeleteRequestToVacateMutation,
  useGetRequestToVacateQuery,
  usePatchRequestToVacateMutation,
} from "services/lien-api";

import RequestToVacateModal from "./RequestToVacateModal";

type RequestToVacateSectionProps = {
  lienId: number;
  organizationId: number;
  accessToken: string;
  requestToVacateAvailable?: boolean;
};

const DECISION_OPTIONS = [
  { value: "APPROVED", label: "Approved" },
  { value: "DENIED", label: "Denied" },
  { value: "PENDING", label: "Pending" },
];

const APPEAL_LEVEL_OPTIONS = [
  { value: "CRC", label: "CRC" },
  { value: "QRC", label: "QRC" },
];

type RequestToVacateFormValues = {
  [key: string]: string | number | null;
};

const RequestToVacateSection = ({
  lienId,
  organizationId,
  requestToVacateAvailable = true,
}: RequestToVacateSectionProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<number | null>(null);
  const [createRequestToVacate] = useCreateRequestToVacateMutation();
  const [patchRequestToVacate] = usePatchRequestToVacateMutation();
  const [deleteRequestToVacate] = useDeleteRequestToVacateMutation();
  const { data: requestToVacateRecords } = useGetRequestToVacateQuery({
    lienId,
  });

  const { control, reset } = useForm<RequestToVacateFormValues>();

  useEffect(() => {
    if (requestToVacateRecords) {
      const defaultValues = requestToVacateRecords.results.reduce(
        (acc, record, index) => {
          acc[`request_to_vacate_due_date_${index}`] =
            record.request_to_vacate_due_date;
          acc[`request_to_vacate_date_${index}`] =
            record.request_to_vacate_date;
          acc[`request_to_vacate_amount_${index}`] =
            record.request_to_vacate_amount;
          acc[`request_to_vacate_savings_${index}`] =
            record.request_to_vacate_savings;
          acc[`request_to_vacate_decision_${index}`] =
            record.request_to_vacate_decision;
          acc[`appeal_level_${index}`] = record.appeal_level;
          return acc;
        },
        {} as RequestToVacateFormValues,
      );

      reset(defaultValues);
    }
  }, [requestToVacateRecords, reset]);

  const handleSubmit = async (data: RequestToVacate) => {
    try {
      await createRequestToVacate({
        data,
      }).unwrap();
    } catch (error) {
      console.error("Failed to create request to vacate:", error);
    }
  };

  const handleFieldChange = async (
    recordId: number,
    field: string,
    value: unknown,
  ) => {
    try {
      // handle select values which come as objects with value and label
      const finalValue =
        typeof value === "object" && value !== null && "value" in value
          ? (value as { value: string }).value
          : value;

      await patchRequestToVacate({
        id: recordId,
        data: {
          [field]: finalValue,
        },
      }).unwrap();
    } catch (error) {
      console.error("Failed to update request to vacate:", error);
    }
  };

  const handleDelete = async (id: number) => {
    setSelectedRecordId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRecordId) return;

    try {
      await deleteRequestToVacate({
        id: selectedRecordId,
      }).unwrap();
      setDeleteModalOpen(false);
      KaboodleToaster.success("Request to vacate deleted successfully");
    } catch (error) {
      console.error("Failed to delete request to vacate:", error);
      KaboodleToaster.error("Failed to delete request to vacate");
    }
  };

  // if there are no records and request_to_vacate_available is false, hide the section
  if (!requestToVacateRecords?.results?.length && !requestToVacateAvailable) {
    return null;
  }

  return (
    <>
      <div className="header-primary flex jc-sb ai-c">
        Request to Vacate
        {requestToVacateAvailable && (
          <div className="flex gap-sm jc-end">
            <Tooltip content="Add new request to vacate">
              <ButtonIcon
                kind="neutral"
                size="small"
                onClick={() => setIsModalOpen(true)}
                type="button"
              >
                add_circle
              </ButtonIcon>
            </Tooltip>
          </div>
        )}
      </div>

      {requestToVacateRecords?.results?.map((record, index) => (
        <Fragment key={record.id}>
          {index > 0 && <SectionBreak />}
          <div className="flex jc-se ai-c">
            <div className="text-sm text-gray-500">
              Request to Vacate #{index + 1}
            </div>
            <Tooltip content="Delete this request to vacate">
              <ButtonIcon
                kind="neutral"
                size="small"
                className="delete"
                onClick={() => handleDelete(record.id)}
                type="button"
              >
                delete
              </ButtonIcon>
            </Tooltip>
          </div>
          <ControlledDateInput
            control={control}
            name={`request_to_vacate_due_date_${index}`}
            label="Due Date"
            errors={{}}
            allowFutureDates
            onSave={async (value) => {
              await handleFieldChange(
                record.id,
                "request_to_vacate_due_date",
                value,
              );
            }}
          />
          <ControlledDateInput
            control={control}
            name={`request_to_vacate_date_${index}`}
            label="Request Date"
            readOnly={true}
            errors={{}}
            allowFutureDates
            onSave={async (value) => {
              await handleFieldChange(
                record.id,
                "request_to_vacate_date",
                value,
              );
            }}
          />
          <ControlledInput
            control={control}
            name={`request_to_vacate_amount_${index}`}
            label="Amount"
            errors={{}}
            componentType={MaskedInput}
            mask="dollar"
            onSave={async (value) => {
              await handleFieldChange(
                record.id,
                "request_to_vacate_amount",
                value,
              );
            }}
          />
          <ControlledSelect
            control={control}
            name={`request_to_vacate_decision_${index}`}
            label="Decision"
            errors={{}}
            options={DECISION_OPTIONS}
            onSave={async (value) => {
              await handleFieldChange(
                record.id,
                "request_to_vacate_decision",
                value,
              );
            }}
          />
          <ControlledInput
            control={control}
            name={`request_to_vacate_savings_${index}`}
            label="Savings"
            errors={{}}
            componentType={MaskedInput}
            mask="dollar"
            onSave={async (value) => {
              await handleFieldChange(
                record.id,
                "request_to_vacate_savings",
                value,
              );
            }}
          />
          <ControlledSelect
            control={control}
            name={`appeal_level_${index}`}
            label="Appeal Level"
            errors={{}}
            options={APPEAL_LEVEL_OPTIONS}
            onSave={async (value) => {
              await handleFieldChange(record.id, "appeal_level", value);
            }}
          />
        </Fragment>
      ))}

      <RequestToVacateModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        lienId={lienId}
        organizationId={organizationId}
      />

      <KaboodleModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        footer={
          <div className="flex jc-c">
            <Button onClick={handleConfirmDelete}>Delete</Button>
            <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          </div>
        }
      >
        <Card heading="Delete Request to Vacate">
          <p>Are you sure you want to delete this request to vacate?</p>
        </Card>
      </KaboodleModal>
    </>
  );
};

export default RequestToVacateSection;
